package gr.tech.meetinggenie.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import gr.tech.meetinggenie.util.CONSTANTS
import gr.tech.meetinggenie.util.CONSTANTS.FONT_FAMILY
import gr.tech.meetinggenie.util.CONSTANTS.FONT_FAMILY2
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent

/* ----------- Init ------------ */

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyleBase("body") {
        Modifier.fontFamily(FONT_FAMILY)
    }

    ctx.stylesheet.registerStyleBase("html") {
        Modifier.scrollBehavior(ScrollBehavior.Smooth)
    }

    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier
            .borderTop(1.px, LineStyle.Solid, Colors.White)
            .opacity(75.percent)
            .fillMaxWidth()
    }
}

/* ----------- Utility ------------ */

fun Modifier.noBorder(): Modifier = this
    .border(width = 0.px, style = LineStyle.None, color = Colors.Transparent)
    .outline(width = 0.px, style = LineStyle.None, color = Colors.Transparent)

/* ----------- Typography — General ------------ */

val SimpleSpanStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.cssRem)
            .fontWeight(FontWeight.Light)
    }
}

/** Primary H1 on a meeting detail page. */
val PageTitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .fontSize(1.8.cssRem)
            .margin(bottom = 4.px)
    }
}

/** Gray H3 subtitle (user name) beneath the page title. */
val PageSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontSize(1.cssRem)
            .color(Colors.Gray)
            .margin(0.px)
    }
}

/** H1 on list / overview pages (e.g. "Weekly Meetings"). */
val ListPageTitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .fontSize(1.5.cssRem)
    }
}

/** H2 used to head major content sections. */
val SectionHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .fontSize(1.4.cssRem)
            .margin(bottom = 8.px)
    }
}

/** H3 used to head sub-sections within a form or card. */
val SubSectionHeadingStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .fontSize(1.1.cssRem)
            .margin(bottom = 12.px)
    }
}

/** H2 inside a modal dialog. */
val ModalTitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .fontSize(1.25.cssRem)
            .margin(bottom = 16.px)
    }
}

/** H3 sub-copy inside a modal dialog (e.g. confirm text). */
val ModalSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontSize(1.cssRem)
            .fontWeight(FontWeight.Medium)
            .margin(bottom = 6.px)
    }
}

/** H1 on the sign-in page. */
val LoginTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(2.cssRem)
            .fontWeight(FontWeight.Bold)
    }
}

val SimpleGridGapStyle = CssStyle {
    base {
        Modifier.gap(8.px)
    }
    Breakpoint.MD {
        Modifier.gap(16.px)
    }
}

/* ----------- Typography — Tooltips ------------ */

val TooltipIconStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .margin(left = 8.px)
    }
}

val ToolTipStyle = CssStyle {
    base {
        Modifier
            .width(375.px)
            .padding(6.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, Colors.LightGray)
    }
}

val ToolTipTextStyle = CssStyle {
    base {
        Modifier
            .color(Colors.Black)
            .fontSize(0.875.cssRem)
    }
}

/* ----------- Typography — Table Cells ------------ */

val TableCellTextStyle = CssStyle {
    base {
        Modifier
            .whiteSpace(WhiteSpace.NoWrap)
            .fontFamily(FONT_FAMILY2)
    }
}

val TableCellBoldTextStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
    }
}

/* ----------- Typography — Form / Status ------------ */

/** Bold label above a form input. */
val FormLabelStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .margin(bottom = 6.px)
    }
}

/** Small inline error message (inside modals / forms). */
val ErrorTextStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .color(Colors.Red)
            .fontSize(0.9.cssRem)
            .margin(bottom = 8.px)
    }
}

/** Full-area error message shown on a page (e.g. invalid meeting ID). */
val ErrorPageStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .color(Colors.Red)
            .padding(24.px)
    }
}

/** Feedback text after save / submit (color is set inline — it is logic-driven). */
val SaveStatusStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .margin(top = 8.px)
    }
}

val SubmittedBadgeStyle = CssStyle {
    base {
        Modifier
            .fontFamily(FONT_FAMILY2)
            .fontWeight(FontWeight.Bold)
            .margin(bottom = 16.px)
    }
}

/** Inline status chip next to a response card name (font only; color is logic-driven). */
val ResponseStatusBadgeStyle = CssStyle {
    base {
        Modifier
            .fontWeight(FontWeight.Bold)
            .margin(left = 8.px)
            .fontSize(0.8.cssRem)
    }
}

/** Pre element used to display raw response data inside a response card. */
val ResponsePreviewStyle = CssStyle {
    base {
        Modifier.fontSize(0.8.cssRem)
    }
}

/* ----------- Components ------------ */

/** White rounded card used for each response in the View Responses section. */
val ResponseCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(bottom = 12.px)
            .padding(16.px)
            .backgroundColor(Colors.White)
            .borderRadius(8.px)
            .border(1.px, LineStyle.Solid, Colors.LightGray)
    }
}

/** Light-gray content card used on list / overview pages. */
val ContentCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.LightGray)
            .borderRadius(8.px)
            .padding(16.px)
    }
}

/** Wraps a table with auto-overflow and a visible border. */
val TableContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .overflow(Overflow.Auto)
            .borderRadius(5.px)
            .border(1.px, LineStyle.Solid, Colors.DarkGray)
    }
}

/** White rounded card that holds modal content (layout dimensions stay inline). */
val ModalCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .borderRadius(20.px)
    }
}

/* ----------- Modal Overlay ------------ */

val ModalOverlayFadeInAnimation = Keyframes {
    from { Modifier.opacity(0) }
    to   { Modifier.opacity(1) }
}

val ModalContentSlideUpAnimation = Keyframes {
    from { Modifier.translateY(20.px).opacity(0) }
    to   { Modifier.translateY(0.px).opacity(1) }
}

val ModalOverlayStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .width(100.percent)
            .height(100.percent)
            .backgroundColor(rgba(0, 0, 0, 0.5))
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .padding(1.cssRem)
            .zIndex(CONSTANTS.DESIGN.MODAL_OVERLAY_INDEX)
            .animation(
                ModalOverlayFadeInAnimation.toAnimation(
                    duration = 0.3.s,
                    timingFunction = AnimationTimingFunction.EaseInOut,
                    iterationCount = AnimationIterationCount.of(1)
                )
            )
    }
}

val ModalContentAnimation = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .animation(
                ModalContentSlideUpAnimation.toAnimation(
                    duration = 0.3.s,
                    timingFunction = AnimationTimingFunction.EaseInOut,
                    iterationCount = AnimationIterationCount.of(1)
                )
            )
    }
}

/** Close ✕ icon inside a modal header. */
val CloseIconStyle = CssStyle {
    base {
        Modifier
            .cursor(Cursor.Pointer)
            .color(Colors.Grey)
    }
}

/** Full-screen dark overlay on the login page. */
val LoginPageStyle = CssStyle {
    base {
        Modifier.backgroundColor(Theme.HalfBlack.rgb)
    }
}

/** White/light card container on the login page. */
val LoginCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Theme.LightGray.rgb)
            .borderRadius(5.px)
    }
}

/** Google sign-in button. */
val GoogleSignInButtonStyle = CssStyle {
    base {
        Modifier
            .height(54.px)
            .color(Colors.White)
            .fontWeight(FontWeight.Medium)
            .fontSize(16.px)
            .backgroundColor(Theme.DarkGreen.rgb)
            .borderRadius(4.px)
            .noBorder()
            .boxShadow(offsetY = 4.px, blurRadius = 8.px, color = rgba(0, 0, 0, 0.2))
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(150.ms))
            .opacity(90.percent)
    }
}

/** Icon inside the Google sign-in button. */
val GoogleSignInIconStyle = CssStyle {
    base {
        Modifier
            .color(Colors.White)
            .margin(right = 10.px)
    }
}

/* ----------- Buttons ------------ */

val ButtonStyle = CssStyle {
    base {
        Modifier
            .whiteSpace(WhiteSpace.NoWrap)
            .margin(top = 16.px)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .backgroundColor(Theme.MediumGreen.rgb)
            .noBorder()
            .borderRadius(4.px)
            .color(Colors.White)
            .fontSize(1.cssRem)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(100.ms))
            .backgroundColor(Theme.DarkGreen.rgb)
    }
}

val WhiteButtonStyle = CssStyle {
    base {
        Modifier
            .margin(top = 16.px)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .borderRadius(4.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .color(Theme.MediumGreen.rgb)
            .fontSize(1.cssRem)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(100.ms))
            .border(1.px, LineStyle.Solid, Theme.DarkGreen.rgb)
            .backgroundColor(Theme.DarkGreen.rgb)
            .color(Colors.White)
    }
}


val SecondaryButtonStyle = CssStyle {
    base {
        Modifier
            .margin(top = 16.px)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .backgroundColor(Colors.LightGray)
            .noBorder()
            .borderRadius(4.px)
            .color(Colors.DarkGray)
            .fontSize(1.cssRem)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(100.ms))
            .backgroundColor(Colors.Gray)
            .color(Colors.White)
    }
}

val GeneralButtonStyle = CssStyle {
    base {
        Modifier
            .boxShadow(0.px, 5.px, 10.px, -5.px, rgba(100, 100, 111, 0.8))
            .backgroundColor(Colors.White)
    }
    hover {
        Modifier
            .transition(Transition.all(300.ms))
            .backgroundColor(Colors.LightGray)
            .scale(0.99)
    }
}

val ShowMoreButtonStyle = CssStyle {
    base {
        Modifier
            .margin(topBottom = 10.px)
            .textAlign(TextAlign.Center)
            .border(width = 1.px, LineStyle.Solid, Colors.Black)
            .padding(topBottom = 10.px, leftRight = 15.px)
            .fontSize(16.px)
            .fontWeight(FontWeight.Medium)
            .cursor(Cursor.Pointer)
    }
}

val MediumGreenHoverLink = CssStyle {
    base {
        Modifier.color(Colors.Black)
    }
    hover {
        Modifier.textDecorationLine(TextDecorationLine.Underline)
    }
}

/* ----------- Dropdown Styles ------------ */


val DropdownColumnStyle = CssStyle {
    base {
        Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Absolute)
            .top(100.percent).right(0.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, Colors.LightGray)
            .borderRadius(6.px)
            .padding(4.px)
            .minWidth(130.px)
            .zIndex(52)
            .boxShadow(offsetY = 4.px, blurRadius = 8.px, color = rgba(0, 0, 0, 0.12))
    }
}

val DropdownOptionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 12.px, topBottom = 8.px)
            .borderRadius(4.px)
            .cursor(Cursor.Pointer)
            .fontFamily(CONSTANTS.FONT_FAMILY2)
            .fontSize(0.875.cssRem)
            //.backgroundColor() - Handled programattically
    }
}

/* ----------- Table Layout Helpers ------------ */

val TableHeaderStyle = CssStyle {
    base {
        Modifier.textAlign(TextAlign.Center)
    }
}

val TableRowHoverStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .outline(width = 2.px, style = LineStyle.Solid, color = Colors.Transparent)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier.outline(width = 2.px, style = LineStyle.Solid, color = Theme.MediumGreen.rgb)
    }
    cssRule(":hover div") {
        Modifier
            .transition(Transition.all(250.ms))
            .opacity(100)
    }
}

val TableRowButtonStyle = CssStyle {
    base {
        Modifier
            .opacity(0)
            .position(Position.Absolute).right(1.cssRem).top(0.px)
            .height(100.percent).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column)
            .justifyContent(JustifyContent.Center)
    }
}

val TableDataStyle = Modifier
    .minHeight(40.px)
    .padding(5.px)
    .textAlign(TextAlign.Start)
    .fontSize(14.px)
    .overflow(Overflow.Hidden)
    .whiteSpace(WhiteSpace.NoWrap)
    .textOverflow(TextOverflow.Ellipsis)

val SmallColumnStyle  = TableDataStyle.width(50.px)
val MediumColumnStyle = TableDataStyle.width(20.percent)
val LargeColumnStyle  = TableDataStyle.width(60.percent).whiteSpace(WhiteSpace.NoWrap)

/** Base styling for a main page data table (replaces Bulma .table). */
val BaseTableStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px)
            .borderCollapse(BorderCollapse.Collapse)
            .backgroundColor(Colors.White)
    }
    cssRule(" th") {
        Modifier
            .padding(leftRight = 12.px, topBottom = 10.px)
            .backgroundColor(Theme.LightGray.rgb)
            .fontWeight(FontWeight.SemiBold)
            .textAlign(TextAlign.Left)
            .borderBottom(2.px, LineStyle.Solid, Colors.LightGray)
    }
    cssRule(" td") {
        Modifier
            .padding(leftRight = 12.px, topBottom = 10.px)
            .borderBottom(1.px, LineStyle.Solid, Colors.LightGray)
    }
}

/* ----------- Form Inputs ------------ */

val InputLabelStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.cssRem)
            .margin(bottom = 2.px)
    }
}

/** Styled text input field (replaces Bulma .input). */
val TextInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontSize(1.cssRem)
            .padding(leftRight = 12.px, topBottom = 8.px)
            .border(1.px, LineStyle.Solid, Colors.LightGray)
            .borderRadius(4.px)
            .backgroundColor(Colors.White)
    }
    focus {
        Modifier
            .outline(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .border(1.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .transition(Transition.all(150.ms))
    }
}

val stringInputStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.875.cssRem)
            .padding(4.px)
            .fillMaxWidth()
    }
    focus {
        Modifier
            .transition(Transition.all(150.ms))
            .outline(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
    }
}

val PropDerivativeLabelStyle = CssStyle {
    base {
        Modifier
            .margin(0.px)
            .fontSize(1.10.cssRem)
            .fontWeight(FontWeight.Bold)
    }
}

val PropDerivativeSpanStyle = CssStyle {
    base {
        Modifier
            .margin(left = 16.px)
            .fontFamily(FONT_FAMILY2)
            .fontSize(1.10.cssRem)
            .fontWeight(FontWeight.Light)
    }
}

val RadioLabelStyle = CssStyle {
    base {
        Modifier
            .margin(right = 10.px)
    }
}
